import { useState } from 'react'
import { Avatar } from '../../../components/Avatar'
import { IngredientCardImage } from '../components/IngredientCardImage'
import { useT } from '../../../i18n'

const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']
const COURSE_NAMES = ['Appetizer', 'Main Course', 'Dessert']

interface Props {
  presenterName: string
  presenterIsAi: boolean
  /** Photo for human presenters; null falls back to initials. */
  presenterAvatar?: string | null
  courseIndex: 0 | 1 | 2
  courseText: string
  /** Ingredient card ids the presenter said they used — shown so
   *  everyone can confirm the dish matches the cards (anti-cheat). */
  usedCardIds?: string[]
  isMyTurn: boolean
  alreadyVoted: boolean
  yesCount: number
  noCount: number
  totalVoters: number
  /** True if this viewer is the host and may move everyone forward. */
  canAdvance: boolean
  /** Host-facing label for the advance button (driven by the phase machine). */
  advanceLabel: string
  onVote: (wouldEat: boolean) => void
  onNextCourse: () => void
  /** Step back to the previous presenter (recovery for over-advancing). */
  onBack?: () => void
  canBack?: boolean
}

export function PresentationScreen({
  presenterName,
  presenterIsAi,
  presenterAvatar,
  courseIndex,
  courseText,
  usedCardIds = [],
  isMyTurn,
  alreadyVoted,
  yesCount,
  noCount,
  totalVoters,
  canAdvance,
  advanceLabel,
  onVote,
  onNextCourse,
  onBack,
  canBack = false,
}: Props) {
  const t = useT()
  const votesIn = yesCount + noCount
  const allVoted = votesIn >= totalVoters
  // Anti-spam: lock the nav for 3s after a press so two people tapping
  // at once can't skip a presenter; Back recovers an over-advance.
  const [navLocked, setNavLocked] = useState(false)
  function lockNav() { setNavLocked(true); setTimeout(() => setNavLocked(false), 3000) }

  return (
    <div className="screen px-5 py-6">
      {/* Presenter header */}
      <div className="flex flex-col items-center mb-6 animate-slide-down">
        <Avatar name={presenterName} avatarUrl={presenterAvatar} isAi={presenterIsAi} size="xl" />
        <h2 className="font-semibold text-brand-text mt-3">{presenterName}</h2>
        <p className="text-sm text-brand-muted">is presenting their {COURSE_NAMES[courseIndex].toLowerCase()}</p>
        {isMyTurn && (
          <span className="mt-2 text-xs font-semibold text-brand-primary bg-brand-primary/10
                           px-3 py-1 rounded-full">
            {t('Your presentation')}
          </span>
        )}
      </div>

      {/* Course card */}
      <div className="card-lg p-6 mb-6 animate-scale-in">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{COURSE_EMOJIS[courseIndex]}</span>
          <span className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
            Course {courseIndex + 1} · {COURSE_NAMES[courseIndex]}
          </span>
        </div>
        <p className="text-lg text-brand-text leading-relaxed">
          "{courseText}"
        </p>
        {usedCardIds.length > 0 && (
          <div className="mt-4 pt-4 border-t border-brand-border">
            <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wide mb-2">
              {t('Ingredients used')}
            </p>
            <div className="flex flex-wrap gap-2">
              {usedCardIds.map(id => (
                <IngredientCardImage key={id} id={id} size="sm" />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Voting */}
      {!isMyTurn && !alreadyVoted && (
        <div className="space-y-3 animate-slide-up">
          <p className="text-center font-semibold text-brand-text">{t('Would you eat this?')}</p>
          <div className="flex gap-3">
            <button onClick={() => onVote(true)} className="vote-yes">
              <div className="text-2xl mb-1">✅</div>
              <div>{t("I'd Eat That!")}</div>
            </button>
            <button onClick={() => onVote(false)} className="vote-no">
              <div className="text-2xl mb-1">🙅</div>
              <div>{t('No Thanks')}</div>
            </button>
          </div>
        </div>
      )}

      {/* Already voted / My turn */}
      {(alreadyVoted || isMyTurn) && (
        <div className="space-y-4 animate-fade-in">
          {/* Live vote counts */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-brand-muted">
                Votes: {votesIn}/{totalVoters}
              </p>
              {allVoted && <span className="text-xs text-brand-success font-semibold">{t('All in!')}</span>}
            </div>
            <div className="flex gap-3">
              <div className="flex-1 text-center">
                <div className="font-display text-3xl font-bold text-iet-green">{yesCount}</div>
                <div className="text-xs text-brand-muted">{t('✅ Would eat')}</div>
              </div>
              <div className="w-px bg-brand-border" />
              <div className="flex-1 text-center">
                <div className="font-display text-3xl font-bold text-red-400">{noCount}</div>
                <div className="text-xs text-brand-muted">{t('🙅 Would not')}</div>
              </div>
            </div>
            {/* Progress bar */}
            {yesCount + noCount > 0 && (
              <div className="mt-3 h-2 rounded-full bg-brand-elevated overflow-hidden">
                <div
                  className="h-full bg-iet-green transition-all duration-500 rounded-full"
                  style={{ width: `${(yesCount / (yesCount + noCount)) * 100}%` }}
                />
              </div>
            )}
          </div>

          {/* Waiting or advance */}
          {!allVoted ? (
            <p className="text-center text-brand-muted text-sm animate-pulse">
              {t('Waiting for votes...')}
            </p>
          ) : canAdvance ? (
            <div className="flex gap-2">
              {canBack && onBack && (
                <button
                  onClick={() => { if (!navLocked) { onBack(); lockNav() } }}
                  disabled={navLocked}
                  className="btn-outline w-auto px-5 disabled:opacity-40"
                >
                  {t('← Back')}
                </button>
              )}
              <button
                onClick={() => { if (!navLocked) { onNextCourse(); lockNav() } }}
                disabled={navLocked}
                className="btn-primary flex-1 disabled:opacity-40"
              >
                {advanceLabel}
              </button>
            </div>
          ) : (
            <p className="text-center text-brand-muted text-sm">
              {t('Waiting for the host to continue...')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
